import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { wsUrl } from "../../lib/api";
import { logSwallow } from "../../lib/log";

/**
 * MatchNotifier — global listener for `match.new` events.
 *
 * Fixes the mutual-BLINK race: whichever user tapped BLINK first only received
 * a background WS event (no navigation). Now both sides land on /match/{id}
 * the moment the reciprocal BLINK closes the loop.
 *
 * Behavior:
 *   • Opens a single /api/ws/user connection once the user is authenticated.
 *   • On `match.new`: if we're not already on /match/{id} or /chat/{id}, we
 *     show a toast and auto-navigate to the mutual-match screen.
 *   • Also mirrors this behavior for the initiator (see pages that call BLINK
 *     directly), so the second-blinker path still navigates normally via its
 *     inline handler; the notifier only takes over when the first-blinker is
 *     idle on another screen.
 */
export default function MatchNotifier() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const wsRef = useRef(null);
  const seenRef = useRef(new Set());
  const locRef = useRef(location);
  locRef.current = location;

  useEffect(() => {
    if (!user?.id) return undefined;
    let ws;
    try {
      const url = wsUrl("/user");
      if (!url) return undefined;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type !== "match.new" || !m.match_id) return;
          // De-dupe: WS can echo on reconnect; we only want to navigate once.
          if (seenRef.current.has(m.match_id)) return;
          seenRef.current.add(m.match_id);
          const path = locRef.current.pathname || "";
          // Don't hijack the flow if the initiator's own inline handler already
          // navigated (they'll already be on /match/{id}) or if they're already
          // chatting with this same match.
          if (path === `/match/${m.match_id}`) return;
          if (path === `/chat/${m.match_id}`) return;
          toast.success("It's a match!", { description: "Both of you blinked — opening now." });
          nav(`/match/${m.match_id}`);
        } catch (e) { logSwallow("match-notifier.parse", e); }
      };
      ws.onerror = (e) => logSwallow("match-notifier.ws.error", e);
    } catch (e) { logSwallow("match-notifier.ws.open", e); }
    return () => {
      if (ws) { try { ws.close(); } catch (e) { logSwallow("match-notifier.ws.close", e); } }
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [user?.id, nav]);

  return null;
}
